import { useRef, useEffect, useMemo } from 'react'
import { Control, FieldValues, Path, useController } from 'react-hook-form'
import { AlertTriangle, ChevronDown } from 'lucide-react'
import { toast } from 'react-toastify'

import {
  CommonEditorProps,
  EditorProps,
  useEditorCore,
  EditorToolbar,
} from 'components/Editor/EditorCore'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import 'components/Editor/Editor.css'

import { colors } from 'styles/tokens'

import {
  findCampaignColumnVariablesWithMissingValues,
  findUnknownTemplateVariables,
  getMissingValuesLabel,
  getMissingValuesMessage,
  mergeVariables,
  type EmailVariable,
} from '../email-variables'
import { useCampaignVariables } from '../columns/useCampaignVariables'
import { useDebouncedFlag } from 'hooks/useDebouncedFlag'

/**
 * Delay validation feedback (inline error banners + Quill background
 * highlighting) by this many ms. Keeps mid-typing states like `{F}` from
 * flashing red the instant the user begins typing a token, and prevents
 * the highlight-wipe-and-reapply from disrupting the editor's selection.
 */
const VALIDATION_DEBOUNCE_MS = 500

interface EmailBodyEditorProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  label?: string
  placeholder?: string
  hideHelperText?: boolean
  campaignId?: number
}

// Quill writes the value out as a literal `background-color` CSS declaration,
// so we pull from the JS token map rather than relying on Tailwind aliasing.
const UNKNOWN_VAR_HIGHLIGHT_BG = colors['error-bg']

/**
 * Remove `background-color` declarations from inline style attributes. The
 * composer applies a red highlight to unknown variables via Quill's
 * `background` format — that highlight is a composer-only affordance and
 * must not survive into the saved sequence HTML.
 */
function stripBackgroundStyles(html: string): string {
  return html.replace(/\sstyle="([^"]*)"/g, (_match, styles: string) => {
    const cleaned = styles
      .split(';')
      .map((rule) => rule.trim())
      .filter((rule) => rule && !/^background(-color)?\s*:/i.test(rule))
      .join('; ')
    return cleaned ? ` style="${cleaned}"` : ''
  })
}

export function EmailBodyEditor<T extends FieldValues>(
  props: EmailBodyEditorProps<T> &
    Omit<CommonEditorProps, 'additionalToolbarButtons'>
) {
  const {
    control,
    name,
    label = 'Body',
    placeholder = 'Write your email content here...',
    hideHelperText = false,
    campaignId,
    ...rest
  } = props

  const customVariables = useCampaignVariables(campaignId)
  const EMAIL_VARIABLES = mergeVariables(customVariables)

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({ control, name })

  const onChangeWithStrip = (next: string | undefined) => {
    onChange(typeof next === 'string' ? stripBackgroundStyles(next) : next)
  }

  const editorProps: EditorProps & CommonEditorProps = {
    ...rest,
    value: value || '',
    onChange: onChangeWithStrip,
    placeholder,
    normalizeForValueSync: stripBackgroundStyles,
    extraPlainTextFormats: ['background'],
  }

  const { uniqueId, quillEditor } = useEditorCore(editorProps)

  // Track cursor position continuously via selection-change and text-change events
  const lastCursorPosition = useRef<number>(0)

  // Always up to date with the current known tokens; the text-change handler
  // reads from this ref so we don't need to detach/reattach on every render.
  const knownTokensRef = useRef<Set<string>>(new Set())
  knownTokensRef.current = new Set(EMAIL_VARIABLES.map((v) => v.token))

  // Debounce the background-highlight pass: re-formatting the entire body
  // through `quill.formatText` on every keystroke disrupts the active
  // selection, so an in-progress token like `{F}` would steal focus the
  // moment it became transiently "unknown". Holding the highlight until
  // typing pauses lets the user finish typing a valid token before any
  // formatting is touched.
  const highlightTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!quillEditor) return

    const updateCursorPosition = () => {
      const selection = quillEditor.getSelection()
      if (selection) {
        lastCursorPosition.current = selection.index + selection.length
      }
    }

    const highlightUnknownTokens = () => {
      const text = quillEditor.getText()
      const totalLen = quillEditor.getLength()
      // Wipe existing highlights first so removed/renamed tokens lose color.
      quillEditor.formatText(0, totalLen, 'background', false, 'silent')
      const tokenRegex = /\{[^{}]+\}/g
      let match: RegExpExecArray | null
      while ((match = tokenRegex.exec(text)) !== null) {
        const tokenName = match[0].replace(/[{}]/g, '')
        if (!knownTokensRef.current.has(tokenName)) {
          quillEditor.formatText(
            match.index,
            match[0].length,
            'background',
            UNKNOWN_VAR_HIGHLIGHT_BG,
            'silent'
          )
        }
      }
    }

    const scheduleHighlight = () => {
      if (highlightTimerRef.current !== null) {
        window.clearTimeout(highlightTimerRef.current)
      }
      highlightTimerRef.current = window.setTimeout(() => {
        highlightTimerRef.current = null
        highlightUnknownTokens()
      }, VALIDATION_DEBOUNCE_MS)
    }

    const handleSelectionChange = (
      range: { index: number; length: number } | null
    ) => {
      if (range) {
        lastCursorPosition.current = range.index + range.length
      }
    }

    const handleTextChange = () => {
      updateCursorPosition()
      scheduleHighlight()
    }

    quillEditor.on('selection-change', handleSelectionChange)
    quillEditor.on('text-change', handleTextChange)

    // Initial pass — content loaded before our handler attached. Runs
    // immediately (no debounce) so existing-content warnings show up
    // on mount without a startup flash.
    highlightUnknownTokens()

    return () => {
      quillEditor.off('selection-change', handleSelectionChange)
      quillEditor.off('text-change', handleTextChange)
      if (highlightTimerRef.current !== null) {
        window.clearTimeout(highlightTimerRef.current)
        highlightTimerRef.current = null
      }
    }
  }, [quillEditor])

  // Track if we just inserted a variable (to focus after dropdown closes)
  const pendingFocusPosition = useRef<number | null>(null)

  const missingVariablesInBody = useMemo(
    () =>
      findCampaignColumnVariablesWithMissingValues(
        EMAIL_VARIABLES,
        typeof value === 'string' ? [value] : []
      ),
    [EMAIL_VARIABLES, value]
  )
  const unknownVariablesInBody = useMemo(
    () =>
      findUnknownTemplateVariables(
        EMAIL_VARIABLES,
        typeof value === 'string' ? [value] : []
      ),
    [EMAIL_VARIABLES, value]
  )
  // Banners stay hidden until the user pauses typing, so transient
  // tokens like `{F}` mid-type do not flash a red banner.
  const showUnknownVariableBanner = useDebouncedFlag(
    unknownVariablesInBody.length > 0,
    VALIDATION_DEBOUNCE_MS
  )
  const showMissingVariableBanner = useDebouncedFlag(
    missingVariablesInBody.length > 0,
    VALIDATION_DEBOUNCE_MS
  )

  const insertVariable = (variable: EmailVariable) => {
    if (!quillEditor) return
    if (variable.hasMissingValues) {
      toast.warning(getMissingValuesMessage(variable))
    }

    // Ensure position is within bounds
    const maxPosition = Math.max(0, quillEditor.getLength() - 1)
    const position = Math.min(lastCursorPosition.current, maxPosition)

    // Insert the variable text
    quillEditor.insertText(position, variable.value)

    // Store position to focus after dropdown closes
    const newPosition = position + variable.value.length
    lastCursorPosition.current = newPosition
    pendingFocusPosition.current = newPosition
  }

  const handleDropdownOpenChange = (open: boolean) => {
    // When dropdown closes, focus editor if we have a pending position
    if (!open && pendingFocusPosition.current !== null && quillEditor) {
      const pos = pendingFocusPosition.current
      pendingFocusPosition.current = null
      // Delay to let dropdown fully release focus
      setTimeout(() => {
        quillEditor.focus()
        quillEditor.setSelection(pos, 0)
      }, 50)
    }
  }

  const variableDropdownButton = (
    <DropdownMenu onOpenChange={handleDropdownOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Insert Variable"
          style={{
            display: 'inline-flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '4px',
            height: '24px',
            padding: '0 8px',
            border: 'none',
            background: 'white',
            fontSize: '12px',
            fontWeight: 500,
            cursor: 'pointer',
            borderRadius: '3px',
          }}
        >
          <span>Variables</span>
          <ChevronDown style={{ width: 12, height: 12, flexShrink: 0 }} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {EMAIL_VARIABLES.map((variable) => (
          <DropdownMenuItem
            key={variable.value}
            onClick={() => insertVariable(variable)}
          >
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate">{variable.label}</span>
              {variable.isCampaignColumn && (
                <span className="shrink-0 rounded bg-surface-hover px-1 text-xxs uppercase text-fg-secondary">
                  {variable.isManual ? 'Manual' : 'AI'}
                </span>
              )}
              {variable.hasMissingValues && (
                <AlertTriangle className="size-3 shrink-0 text-warning" />
              )}
            </span>
            {variable.hasMissingValues && (
              <span className="ml-2 shrink-0 text-xxs text-fg-secondary">
                {getMissingValuesLabel(variable)}
              </span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-fg">{label}</label>
      )}
      {showUnknownVariableBanner && unknownVariablesInBody.length > 0 && (
        <div
          className="flex items-start gap-2 rounded-md border border-error-border bg-error-bg px-3 py-2 text-xs text-error-strong"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-error" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">
              {unknownVariablesInBody.length === 1
                ? 'This variable is not available for this campaign and will not be filled at send time:'
                : 'These variables are not available for this campaign and will not be filled at send time:'}
            </p>
            <div className="mt-1 flex flex-wrap gap-1">
              {unknownVariablesInBody.map((token) => (
                <code
                  key={token}
                  className="rounded border border-error-border bg-error-bg-subtle px-1.5 py-0.5 font-mono text-xxs text-error-strong"
                >
                  {token}
                </code>
              ))}
            </div>
          </div>
        </div>
      )}
      {showMissingVariableBanner && missingVariablesInBody.length > 0 && (
        <div
          className="flex items-start gap-2 rounded-md border border-warning-border bg-warning-bg px-3 py-2 text-xs text-warning-strong"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">
              Campaign column variables in this email are missing values for
              some contacts. Fill those cells before sending:
            </p>
            <div className="mt-1 flex flex-wrap gap-1">
              {missingVariablesInBody.map((v) => (
                <span
                  key={v.token}
                  className="rounded border border-warning-border bg-warning-bg-subtle px-1.5 py-0.5 text-xxs text-warning-strong"
                >
                  {v.label} ({getMissingValuesLabel(v)})
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      <div
        className={`border rounded-md bg-white ${error ? 'border-error' : 'border-line'}`}
      >
        <div
          id={`editor-${uniqueId}`}
          className="min-h-[120px] bg-white [&_.ql-editor]:min-h-[120px] [&_.ql-editor_p]:!mb-0"
        ></div>
        <EditorToolbar
          uniqueId={uniqueId}
          plainText={rest.plainText}
          additionalToolbarButtons={variableDropdownButton}
        />
      </div>
      {error && <p className="text-xs text-error mt-1">{error.message}</p>}
      {!hideHelperText && (
        <p className="text-xs text-fg-secondary">
          Use variables like {'{FIRST_NAME}'} to personalize your emails.
        </p>
      )}
    </div>
  )
}
